/**
 * Insights → Feedback ("Agent feedback").
 *
 * Every like and dislike people gave the agents' answers in the viewer's
 * organizations, in the agent editor's Connectors idiom (section head, cards,
 * pills): a one-line head; one filter bar that never wraps (search, rating,
 * dates, and the rest in a "Filters" popover); then the table, which fills the
 * window down to its bottom and is the page's only scroller — a slim strip of
 * the totals for the current filters and the filters in force, a sticky column
 * head, and rows that load as you scroll (keyset pages from the server, stable
 * under any sort / filter / search); and a large review window
 * for one item — the feedback beside the conversation it came from, opened
 * at once.
 *
 * Gated by `insights.feedback_review` (the page hides the tab; every API route
 * refuses on its own). Message content follows `insights.transcripts`
 * (`content_allowed` on each item, decided by the server).
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker, Input, Popover, Segmented, Select } from 'antd';
import {
  CloseOutlined, DislikeFilled, FilterOutlined, LikeFilled, LockOutlined, MessageOutlined,
  ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import reasonCatalog from '../../../../../../config/message_feedback_reasons.json';
import { bidiIsolate } from '@/utils/bidi';
import { useLanguage } from '@/hooks/useLanguage';
import { datePattern } from '@/utils/locale';
import { mirrorPlacement } from '@/utils/rtl';
import feedbackReviewService from '../../services/feedbackReviewService';
import type {
  FeedbackFacets, FeedbackItem, FeedbackQuery, FeedbackRating, FeedbackSortKey, FeedbackSummary,
} from '../../types/feedback';
import { FeedbackWindow } from './FeedbackWindow';
import { PersonAvatar, RatingBadge, ReasonPills } from './feedbackParts';
import '@modules/chatbots/components/ConnectorDialog.css';
import '@modules/chatbots/components/BotConnectorsTab.css';
import './FeedbackReview.css';

const { RangePicker } = DatePicker;

const PAGE = 30;

/** The popover's selects open their lists inside the popover: on the page they
 *  would open beneath it (the popover is a higher layer), out of reach. */
const inPanel = (trigger: HTMLElement): HTMLElement => trigger.closest<HTMLElement>('.fbr-filters') ?? document.body;
const SKELETON_ROWS = 6;
const LIKE_REASONS: string[] = reasonCatalog.like.map((r) => r.key);
const DISLIKE_REASONS: string[] = reasonCatalog.dislike.map((r) => r.key);

interface Filters {
  q: string;
  rating: FeedbackRating | '';
  chatbotIds: string[];
  tenantIds: string[];
  reasons: string[];
  hasComment: boolean | null;
  range: [Dayjs, Dayjs] | null;
}

const NO_FILTERS: Filters = {
  q: '', rating: '', chatbotIds: [], tenantIds: [], reasons: [], hasComment: null, range: null,
};

/** How many values the "Filters" popover holds in force. */
function popoverCount(f: Filters): number {
  return f.chatbotIds.length + f.tenantIds.length + f.reasons.length + (f.hasComment === null ? 0 : 1);
}

function isFiltered(f: Filters): boolean {
  return Boolean(f.q.trim() || f.rating || f.chatbotIds.length || f.tenantIds.length
    || f.reasons.length || f.hasComment !== null || f.range);
}

type Sort = { key: FeedbackSortKey; order: 'asc' | 'desc' };
const DEFAULT_SORT: Sort = { key: 'rated_at', order: 'desc' };

/** The sortable columns, in the row's order. */
const COLUMNS: Array<{ key: FeedbackSortKey; label: string; className: string }> = [
  { key: 'rating', label: 'insights.feedback.columns.rating', className: 'fbr-col--rating' },
  { key: 'rated_at', label: 'insights.feedback.columns.date', className: 'fbr-col--when' },
  { key: 'agent', label: 'insights.feedback.columns.agent', className: 'fbr-col--agent' },
  { key: 'user', label: 'insights.feedback.columns.user', className: 'fbr-col--user' },
  { key: 'reasons', label: 'insights.feedback.columns.reasons', className: 'fbr-col--reasons' },
  { key: 'comment', label: 'insights.feedback.columns.comment', className: 'fbr-col--comment' },
  { key: 'model', label: 'insights.feedback.columns.model', className: 'fbr-col--model' },
];

export function FeedbackReviewTab() {
  const { t, direction } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('feedback');

  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [facets, setFacets] = useState<FeedbackFacets | null>(null);
  const [facetsFailed, setFacetsFailed] = useState(false);
  const [facetsKey, setFacetsKey] = useState(0);
  const [active, setActive] = useState(0);
  // The open item, by id: its place is read off the list, so a list replaced
  // under the window leaves it on the same feedback (fetched by id).
  const [openId, setOpenId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pageAbort = useRef<AbortController | null>(null);
  const moreAbort = useRef<AbortController | null>(null);
  const generation = useRef(0);

  // Search is typed; ask the server once typing pauses (and only when the
  // text really changed — the first render must not fetch twice).
  useEffect(() => {
    if (searchText === filters.q) return undefined;
    const handle = window.setTimeout(() => setFilters((f) => ({ ...f, q: searchText })), 300);
    return () => window.clearTimeout(handle);
  }, [searchText, filters.q]);

  const range = useMemo(() => (filters.range ? {
    dateFrom: filters.range[0].startOf('day').toISOString(),
    dateTo: filters.range[1].endOf('day').toISOString(),
  } : { dateFrom: null, dateTo: null }), [filters.range]);

  const query: FeedbackQuery = useMemo(() => ({
    q: filters.q,
    rating: filters.rating,
    chatbotIds: filters.chatbotIds,
    tenantIds: filters.tenantIds,
    reasons: filters.reasons,
    hasComment: filters.hasComment,
    ...range,
    sort: sort.key,
    order: sort.order,
    limit: PAGE,
  }), [filters, range, sort]);

  // A new query starts over at the top: first page, totals, scroll reset.
  useEffect(() => {
    pageAbort.current?.abort();
    moreAbort.current?.abort();
    const ctrl = new AbortController();
    pageAbort.current = ctrl;
    const gen = ++generation.current;
    // Nothing of the previous query survives: not its rows, its totals, nor
    // the cursor a failed first page would otherwise hand to the next one.
    setLoading(true);
    setLoadingMore(false);
    setMoreFailed(false);
    setError(false);
    setItems([]);
    setSummary(null);
    setCursor(null);
    setHasMore(false);
    setActive(0);
    scrollerRef.current?.scrollTo({ top: 0 });
    feedbackReviewService.list(query, ctrl.signal)
      .then((page) => {
        if (gen !== generation.current) return;
        setItems(page.items);
        setSummary(page.summary);
        setCursor(page.next_cursor);
        setHasMore(page.has_more);
      })
      .catch((err) => {
        if (ctrl.signal.aborted || err?.name === 'CanceledError') return;
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (gen === generation.current) setLoading(false);
      });
    return () => ctrl.abort();
  }, [query, reloadKey]);

  const loadMore = useCallback(() => {
    if (!hasMore || !cursor || loading || loadingMore || moreFailed || error) return;
    const gen = generation.current;
    const ctrl = new AbortController();
    moreAbort.current = ctrl;
    setLoadingMore(true);
    feedbackReviewService.list({ ...query, cursor }, ctrl.signal)
      .then((page) => {
        if (gen !== generation.current) return;
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.feedback_id));
          return [...prev, ...page.items.filter((i) => !seen.has(i.feedback_id))];
        });
        setCursor(page.next_cursor);
        setHasMore(page.has_more);
      })
      .catch(() => {
        // Said, not hidden: the list must never read as complete when it is not.
        if (gen === generation.current && !ctrl.signal.aborted) setMoreFailed(true);
      })
      .finally(() => {
        if (gen === generation.current) setLoadingMore(false);
      });
  }, [hasMore, cursor, loading, loadingMore, moreFailed, error, query]);

  // Nothing in flight outlives the tab.
  useEffect(() => () => { pageAbort.current?.abort(); moreAbort.current?.abort(); }, []);

  // The next page loads as the end of the list comes near.
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !scrollerRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root: scrollerRef.current, rootMargin: '480px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, items.length]);

  useEffect(() => {
    let cancelled = false;
    setFacetsFailed(false);
    feedbackReviewService.facets(range)
      .then((f) => { if (!cancelled) setFacets(f); })
      .catch(() => { if (!cancelled) { setFacets(null); setFacetsFailed(true); } });
    return () => { cancelled = true; };
  }, [range, facetsKey]);

  const update = useCallback((patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch })), []);

  const toggleSort = (key: FeedbackSortKey) => {
    setSort((s) => {
      if (s.key !== key) return { key, order: 'desc' };
      if (s.order === 'desc') return { key, order: 'asc' };
      return DEFAULT_SORT;
    });
  };

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const openAt = useCallback((index: number) => {
    setActive(index);
    setOpenId(itemsRef.current[index]?.feedback_id ?? null);
  }, []);

  const closeLink = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('feedback');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || !items.length) return;
    let next = active;
    if (e.key === 'ArrowDown') next = Math.min(items.length - 1, active + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, active - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAt(active);
      return;
    } else return;
    e.preventDefault();
    setActive(next);
    document.getElementById(`fbr-row-${items[next].feedback_id}`)?.scrollIntoView({ block: 'nearest' });
    if (next >= items.length - 3) loadMore();
  };

  // The names people wrote, each in its own direction and cut at its own end:
  // the agent first, its organization as quiet second text (in the list) or a
  // tooltip (on a chosen value). `search` is what typing in the select matches.
  const agentNames = useMemo(() => new Map((facets?.agents || [])
    .filter((a) => a.chatbot_id)
    .map((a) => [a.chatbot_id as string, { name: a.agent || t('insights.feedback.noAgent'), org: a.organization || '' }])),
  [facets, t]);
  const orgNames = useMemo(() => new Map((facets?.organizations || [])
    .map((o) => [o.tenant_id, o.organization || o.tenant_id])), [facets]);
  const agentOptions = useMemo(() => [...agentNames].map(([id, a]) => ({
    value: id,
    search: `${a.name} ${a.org}`,
    label: (
      <span className="fbr-opt">
        <bdi dir="auto" className="fbr-opt__name">{a.name}</bdi>
        {a.org && <bdi dir="auto" className="fbr-opt__org">{a.org}</bdi>}
      </span>
    ),
    chosen: <bdi dir="auto" title={a.org ? `${a.name} · ${a.org}` : a.name}>{a.name}</bdi>,
  })), [agentNames]);
  const orgOptions = useMemo(() => [...orgNames].map(([id, name]) => ({
    value: id, search: name, label: <bdi dir="auto" title={name}>{name}</bdi>,
  })), [orgNames]);
  const reasonOptions = useMemo(() => {
    const opt = (key: string) => ({ value: key, label: t(`chat.rating.reasons.${key}`) });
    return [
      { label: t('insights.feedback.filters.reasonsOnLiked'), options: LIKE_REASONS.map(opt) },
      { label: t('insights.feedback.filters.reasonsOnDisliked'), options: DISLIKE_REASONS.map(opt) },
    ];
  }, [t]);

  // The popover's values in force, each removable from the strip.
  // A tag is the field (UI text, the page's direction) and the value (a name
  // someone wrote: its own direction, cut at its own end, whole in the tooltip).
  const filterTags = useMemo(() => {
    const field = (k: string) => t(`insights.feedback.filters.${k}`);
    return [
      ...filters.chatbotIds.map((id) => {
        const a = agentNames.get(id);
        return {
          key: `a:${id}`, field: field('agent'), value: a?.name ?? id,
          title: a?.org ? `${a.name} · ${a.org}` : (a?.name ?? id),
          remove: () => update({ chatbotIds: filters.chatbotIds.filter((x) => x !== id) }),
        };
      }),
      ...filters.tenantIds.map((id) => ({
        key: `o:${id}`, field: field('organization'), value: orgNames.get(id) ?? id, title: orgNames.get(id) ?? id,
        remove: () => update({ tenantIds: filters.tenantIds.filter((x) => x !== id) }),
      })),
      ...filters.reasons.map((r) => ({
        key: `r:${r}`, field: field('reason'), value: t(`chat.rating.reasons.${r}`), title: t(`chat.rating.reasons.${r}`),
        remove: () => update({ reasons: filters.reasons.filter((x) => x !== r) }),
      })),
      ...(filters.hasComment === null ? [] : [{
        key: 'c', field: field('comment'),
        value: filters.hasComment ? field('commentWith') : field('commentWithout'),
        title: filters.hasComment ? field('commentWith') : field('commentWithout'),
        remove: () => update({ hasComment: null }),
      }]),
    ];
  }, [filters, agentNames, orgNames, t, update]);

  const filtered = isFiltered(filters);
  const empty = !loading && !error && items.length === 0;
  const activeId = items[active] ? `fbr-row-${items[active].feedback_id}` : undefined;
  const openIndex = openId ? items.findIndex((i) => i.feedback_id === openId) : -1;

  return (
    <div className="fbr-host">
      <div className="fbr" data-testid="feedback-review">
        <header className="fbr__head">
          <div className="fbr__heading">
            <span className="cxs__icon fbr__icon" aria-hidden="true"><MessageOutlined /></span>
            <h2 className="fbr__title">{t('insights.feedback.title')}</h2>
            <p className="fbr__desc" title={t('insights.feedback.subtitle')}>{t('insights.feedback.subtitle')}</p>
          </div>
        </header>

        <section className="fbr__bar" aria-label={t('insights.feedback.toolbar')} data-testid="feedback-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined aria-hidden />}
            placeholder={t('insights.feedback.searchPlaceholder')}
            aria-label={t('insights.feedback.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="fbr__search"
            dir="auto"
            data-testid="feedback-search"
          />
          <Segmented
            value={filters.rating || 'all'}
            onChange={(v) => update({ rating: v === 'all' ? '' : (v as FeedbackRating) })}
            options={[
              { value: 'all', label: t('insights.feedback.filters.ratingAll') },
              { value: 'like', label: <span className="fbr__seg"><LikeFilled aria-hidden />{t('insights.feedback.filters.ratingLike')}</span> },
              { value: 'dislike', label: <span className="fbr__seg"><DislikeFilled aria-hidden />{t('insights.feedback.filters.ratingDislike')}</span> },
            ]}
            aria-label={t('insights.feedback.filters.rating')}
            className="fbr__fixed"
            data-testid="feedback-rating-filter"
          />
          <RangePicker
            value={filters.range}
            onChange={(v) => update({ range: v && v[0] && v[1] ? [v[0], v[1]] : null })}
            allowClear
            presets={[
              { label: t('insights.feedback.filters.last7'), value: [dayjs().subtract(6, 'day'), dayjs()] },
              { label: t('insights.feedback.filters.last30'), value: [dayjs().subtract(29, 'day'), dayjs()] },
              { label: t('insights.feedback.filters.last90'), value: [dayjs().subtract(89, 'day'), dayjs()] },
            ]}
            format={datePattern('monthDayYear')}
            aria-label={t('insights.feedback.filters.dateRange')}
            className="fbr__fixed"
          />
          <Popover
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            trigger="click"
            placement={mirrorPlacement(direction, 'bottomRight')}
            arrow={false}
            classNames={{ root: 'fbr-filters-pop' }}
            content={(
              <div className="fbr-filters" data-testid="feedback-filters-panel">
                <label className="fbr-filters__field">
                  <span>{t('insights.feedback.filters.agent')}</span>
                  <Select
                    getPopupContainer={inPanel}
                    mode="multiple" allowClear
                    placeholder={t('insights.feedback.filters.agent')} aria-label={t('insights.feedback.filters.agent')}
                    options={agentOptions} value={filters.chatbotIds} onChange={(v) => update({ chatbotIds: v })}
                    optionFilterProp="search" optionLabelProp="chosen" data-testid="feedback-agent-filter"
                  />
                </label>
                {orgOptions.length > 1 && (
                  <label className="fbr-filters__field">
                    <span>{t('insights.feedback.filters.organization')}</span>
                    <Select
                      getPopupContainer={inPanel}
                      mode="multiple" allowClear
                      placeholder={t('insights.feedback.filters.organization')}
                      aria-label={t('insights.feedback.filters.organization')}
                      options={orgOptions} value={filters.tenantIds} onChange={(v) => update({ tenantIds: v })}
                      optionFilterProp="search"
                    />
                  </label>
                )}
                <label className="fbr-filters__field">
                  <span>{t('insights.feedback.filters.reason')}</span>
                  <Select
                    getPopupContainer={inPanel}
                    mode="multiple" allowClear
                    placeholder={t('insights.feedback.filters.reason')} aria-label={t('insights.feedback.filters.reason')}
                    options={reasonOptions} value={filters.reasons} onChange={(v) => update({ reasons: v })}
                    data-testid="feedback-reason-filter"
                  />
                </label>
                <label className="fbr-filters__field">
                  <span>{t('insights.feedback.filters.comment')}</span>
                  <Select
                    getPopupContainer={inPanel}
                    value={filters.hasComment === null ? 'any' : (filters.hasComment ? 'with' : 'without')}
                    onChange={(v) => update({ hasComment: v === 'any' ? null : v === 'with' })}
                    aria-label={t('insights.feedback.filters.comment')}
                    options={[
                      { value: 'any', label: t('insights.feedback.filters.commentAny') },
                      { value: 'with', label: t('insights.feedback.filters.commentWith') },
                      { value: 'without', label: t('insights.feedback.filters.commentWithout') },
                    ]}
                    data-testid="feedback-comment-filter"
                  />
                </label>
                {facetsFailed && (
                  <p className="fbr__facets-failed" role="status">
                    {t('insights.feedback.filters.loadFailed')}{' '}
                    <button type="button" className="cxc__link" onClick={() => setFacetsKey((k) => k + 1)}>
                      {t('insights.feedback.error.retry')}
                    </button>
                  </p>
                )}
                <div className="fbr-filters__foot">
                  <button
                    type="button" className="cxc__link" disabled={!popoverCount(filters)}
                    onClick={() => update({ chatbotIds: [], tenantIds: [], reasons: [], hasComment: null })}
                  >
                    {t('insights.feedback.filters.reset')}
                  </button>
                  <button type="button" className="cx-btn cx-btn--sm" onClick={() => setFiltersOpen(false)}>
                    {t('insights.feedback.filters.done')}
                  </button>
                </div>
              </div>
            )}
          >
            <button
              type="button"
              className={`cx-btn fbr__fixed fbr__filters-btn${popoverCount(filters) ? ' is-set' : ''}`}
              aria-haspopup="dialog"
              aria-expanded={filtersOpen}
              data-testid="feedback-filters-button"
            >
              <FilterOutlined aria-hidden /> {t('insights.feedback.filters.button')}
              {popoverCount(filters) > 0 && (
                <span className="fbr__count" aria-label={t('insights.feedback.filters.inForce', { count: popoverCount(filters) })}>
                  {popoverCount(filters)}
                </span>
              )}
            </button>
          </Popover>
        </section>

        <div className="fbr__table" data-testid="feedback-table">
          <div
            ref={scrollerRef}
            className="fbr__scroller"
            aria-busy={loading || loadingMore}
            data-testid="feedback-scroller"
          >
            <div className="fbr__strip" data-testid="feedback-summary" aria-live="polite">
              <dl className="fbr__totals">
                <div className="fbr__total">
                  <dd>{summary ? summary.total : '—'}</dd><dt>{t('insights.feedback.summary.total')}</dt>
                </div>
                <span className="fbr__sep" aria-hidden="true" />
                <div className="fbr__total fbr__total--like">
                  <dd><LikeFilled aria-hidden /> {summary ? summary.likes : '—'}</dd><dt>{t('insights.feedback.summary.likes')}</dt>
                </div>
                <div className="fbr__total fbr__total--dislike">
                  <dd><DislikeFilled aria-hidden /> {summary ? summary.dislikes : '—'}</dd><dt>{t('insights.feedback.summary.dislikes')}</dt>
                </div>
                {summary?.like_rate != null && (
                  <span
                    className="fbr__meter"
                    role="img"
                    aria-label={t('insights.feedback.summary.meter', { rate: Math.round(summary.like_rate * 100) })}
                  >
                    <span className="fbr__meter-fill" style={{ inlineSize: `${Math.round(summary.like_rate * 100)}%` }} />
                  </span>
                )}
                <div className="fbr__total">
                  <dd>{summary?.like_rate == null ? '—' : `${Math.round(summary.like_rate * 100)}%`}</dd>
                  <dt>{t('insights.feedback.summary.likeRate')}</dt>
                </div>
                <span className="fbr__sep" aria-hidden="true" />
                <div className="fbr__total">
                  <dd>{summary ? summary.with_comment : '—'}</dd><dt>{t('insights.feedback.summary.comments')}</dt>
                </div>
              </dl>
              {filtered && (
                <div className="fbr__in-force">
                  <ul className="fbr__tags" aria-label={t('insights.feedback.filters.inForceLabel')}>
                    {filterTags.map((tag) => (
                      <li key={tag.key} className="fbr__tag" title={tag.title}>
                        <span className="fbr__tag-field">{tag.field}:</span>
                        <bdi dir="auto" className="fbr__tag-value">{tag.value}</bdi>
                        <button type="button" className="fbr__tag-x" onClick={tag.remove}
                          aria-label={t('insights.feedback.filters.remove', { name: `${tag.field}: ${bidiIsolate(tag.value)}` })}>
                          <CloseOutlined aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="cxc__link" onClick={() => { setSearchText(''); setFilters(NO_FILTERS); }}>
                    {t('insights.feedback.filters.reset')}
                  </button>
                </div>
              )}
            </div>
            <div className="fbr__cols">
              {COLUMNS.map((c) => {
                const on = sort.key === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    className={`fbr__col ${c.className}${on ? ' is-sorted' : ''}`}
                    onClick={() => toggleSort(c.key)}
                    aria-label={t('insights.feedback.sortBy', { column: t(c.label) })}
                    data-testid={`feedback-sort-${c.key}`}
                  >
                    <span>{t(c.label)}</span>
                    <span className="fbr__sort" aria-hidden>{on ? (sort.order === 'desc' ? '↓' : '↑') : ''}</span>
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="fbr__state" role="alert">
                <p>{t('insights.feedback.error.title')}</p>
                <button type="button" className="cx-btn" onClick={() => setReloadKey((k) => k + 1)}>
                  <ReloadOutlined aria-hidden /> {t('insights.feedback.error.retry')}
                </button>
              </div>
            )}

            {empty && (
              <div className="fbr__empty" data-testid="feedback-empty">
                <span className="cxs__icon" aria-hidden="true">{filtered ? <SearchOutlined /> : <MessageOutlined />}</span>
                <p className="fbr__empty-title">
                  {filtered ? t('insights.feedback.empty.filtered') : t('insights.feedback.empty.title')}
                </p>
                <p className="cxs__desc">
                  {filtered ? t('insights.feedback.empty.filteredHint') : t('insights.feedback.empty.description')}
                </p>
              </div>
            )}

            <div
              ref={listRef}
              className="fbr__rows"
              role="listbox"
              tabIndex={0}
              aria-label={t('insights.feedback.listLabel')}
              aria-activedescendant={activeId}
              onKeyDown={onListKey}
              data-testid="feedback-list"
            >
              {items.map((item, index) => (
                <FeedbackRow
                  key={item.feedback_id}
                  item={item}
                  index={index}
                  active={index === active}
                  onOpen={openAt}
                />
              ))}
            </div>

            {(loading || loadingMore) && Array.from({ length: loading ? SKELETON_ROWS : 2 }, (_, i) => (
              <div key={`sk-${i}`} className="fbr-row fbr-row--skeleton" aria-hidden="true">
                {COLUMNS.map((c) => <span key={c.key} className={`fbr-skel ${c.className}`} />)}
              </div>
            ))}

            <div ref={sentinelRef} className="fbr__sentinel" aria-hidden="true" />
            {moreFailed && (
              <p className="fbr__end" role="status">
                {t('insights.feedback.loadMoreFailed')}{' '}
                <button type="button" className="cxc__link" onClick={() => setMoreFailed(false)}>
                  {t('insights.feedback.error.retry')}
                </button>
              </p>
            )}
            {!loading && !hasMore && items.length > 0 && (
              <p className="fbr__end">{t('insights.feedback.endOfList', { count: items.length })}</p>
            )}
          </div>
        </div>

        {(openId || linkedId) && (
          <FeedbackWindow
            items={items}
            index={openIndex >= 0 ? openIndex : null}
            linkedId={openIndex >= 0 ? null : (openId ?? linkedId)}
            hasMore={hasMore && !moreFailed}
            total={summary?.total ?? items.length}
            onLoadMore={loadMore}
            onIndexChange={openAt}
            onClose={(lastId) => {
              setOpenId(null);
              if (linkedId) closeLink();
              const row = lastId ? document.getElementById(`fbr-row-${lastId}`) : null;
              row?.scrollIntoView({ block: 'nearest' });
              listRef.current?.focus();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Memoised: typing a search or moving the cursor re-renders only the rows it changes.
const FeedbackRow = memo(function FeedbackRow({ item, index, active, onOpen }: {
  item: FeedbackItem; index: number; active: boolean; onOpen: (index: number) => void;
}) {
  const { t } = useLanguage();
  return (
    <div
      id={`fbr-row-${item.feedback_id}`}
      role="option"
      aria-selected={active}
      className={`fbr-row${active ? ' is-active' : ''}`}
      onClick={() => onOpen(index)}
      data-testid="feedback-row"
    >
      <span className="fbr-col--rating"><RatingBadge rating={item.rating} compact /></span>
      <span className="fbr-col--when">
        <span className="fbr-row__date">{item.rated_at ? dayjs(item.rated_at).format(datePattern('monthDayYear')) : '—'}</span>
        <span className="fbr-row__sub">
          {item.rated_at ? dayjs(item.rated_at).format('HH:mm') : ''}
          {item.edited && <span className="cx-pill fbr-row__edited">{t('insights.feedback.edited')}</span>}
        </span>
      </span>
      <span className="fbr-col--agent">
        <span className="fbr-row__strong" dir="auto">{item.agent || t('insights.feedback.noAgent')}</span>
        <span className="fbr-row__sub" dir="auto">{item.organization || '—'}</span>
      </span>
      <span className="fbr-col--user">
        <PersonAvatar name={item.user_name} />
        <span className="fbr-row__name" dir="auto">{item.user_name || t('insights.feedback.unknownUser')}</span>
      </span>
      <span className="fbr-col--reasons"><ReasonPills reasons={item.reasons} rating={item.rating} /></span>
      <span className="fbr-col--comment">
        {item.comment
          ? <span className="fbr-row__comment" dir="auto">{item.comment}</span>
          : <span className="fbr-row__none">—</span>}
        {!item.content_allowed && (
          <span className="fbr-row__lock">
            <LockOutlined aria-hidden /> {t('insights.feedback.contentOffShort')}
          </span>
        )}
      </span>
      <span className="fbr-col--model">
        {item.model
          ? <span className="fbr-row__sub" dir="auto" title={item.model}>{item.model_name || item.model}</span>
          : <span className="fbr-row__none">—</span>}
      </span>
    </div>
  );
});

export default FeedbackReviewTab;
